/**
 * Pictures a homeowner brings.
 *
 * A render they made in whatever tool they like, a photo of the room as it is, a photo of
 * the builder's plan. None of it is ours; all of it is theirs, and it travels with the design
 * -- to the maker at sign-off, and (for a plan) to the reader that turns it into walls.
 *
 * Two jobs here, both on the device: get the picture in, and make it small enough to carry.
 * A phone photo is 4000 pixels and 3MB; nothing here needs more than 1600 and a few hundred
 * kilobytes, and a sign-off payload that is mostly photographs is a sign-off that fails to
 * save on the first phone with a full localStorage.
 */
/**
 * What a picture is FOR. Every one of them is an input to the design, not an attachment:
 * a plan is read for the walls, a photo of the room for the walls and what is already there,
 * a render for which of this maker's finishes it is nearest. All three still travel to the
 * maker at sign-off, because a reference is worth having.
 */
export const KINDS = [
  { id: 'plan', name: 'A floor plan', blurb: 'The builder’s drawing, or a sketch with numbers. Read for the walls, then designed.' },
  { id: 'photo', name: 'A photo of the room', blurb: 'As it is. Read for the room’s size and what is already where.' },
  { id: 'render', name: 'A kitchen I like', blurb: 'A render from any tool, or a picture. Matched to this maker’s doors, colours and benchtops.' },
]

export const MAX_EDGE = 1600
export const MAX_BYTES = 400 * 1024
export const MAX_TOTAL = 2 * 1024 * 1024

/**
 * Ask for a picture: a data URL, or null if they changed their mind.
 *
 * A plain file input, everywhere. On a phone the OS answers it with its own sheet -- Photo
 * Library, Take Photo, Choose File -- which is exactly the three things a person might have:
 * a screenshot of a render, a photo they took last week, the builder's PDF-turned-JPEG in
 * their files. The `capture` attribute is deliberately absent: with it, iOS opens the camera
 * straight away and the gallery is never offered. On a laptop it is the file dialog.
 */
export function pickImage() {
  return new Promise((resolve) => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'image/*'
    input.style.display = 'none'
    document.body.appendChild(input)
    let settled = false
    const done = (v) => { if (settled) return; settled = true; input.remove(); resolve(v) }
    input.addEventListener('change', () => {
      const f = input.files?.[0]
      if (!f) return done(null)
      const r = new FileReader()
      r.onload = () => done(String(r.result))
      r.onerror = () => done(null)
      r.readAsDataURL(f)
    })
    // No change event fires on cancel in most browsers; focus coming back is the best signal,
    // given a moment for a slow change event to arrive first.
    window.addEventListener('focus', () => setTimeout(() => { if (!input.files?.length) done(null) }, 1200), { once: true })
    input.click()
  })
}

/**
 * Shrink a picture to what the design needs.
 *
 * Longest edge to `MAX_EDGE`, then JPEG at falling quality until it is under `MAX_BYTES`.
 * Returns the data URL and its size. Runs on a canvas, so it needs a document; that is fine,
 * because the only place a picture arrives is a screen.
 */
export async function shrink(dataUrl, { maxEdge = MAX_EDGE, maxBytes = MAX_BYTES } = {}) {
  const img = await load(dataUrl)
  const scale = Math.min(1, maxEdge / Math.max(img.width, img.height))
  const w = Math.round(img.width * scale), h = Math.round(img.height * scale)
  const c = document.createElement('canvas')
  c.width = w; c.height = h
  const ctx = c.getContext('2d')
  ctx.drawImage(img, 0, 0, w, h)
  let q = 0.86
  let out = c.toDataURL('image/jpeg', q)
  while (bytesOf(out) > maxBytes && q > 0.4) {
    q -= 0.1
    out = c.toDataURL('image/jpeg', q)
  }
  return { dataUrl: out, bytes: bytesOf(out), width: w, height: h }
}

function load(src) {
  return new Promise((resolve, reject) => {
    const img = new window.Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error('That file is not a picture this phone can open.'))
    img.src = src
  })
}

/** How big a data URL is once decoded -- what it costs to store, not its string length. */
export function bytesOf(dataUrl) {
  const i = dataUrl.indexOf(',')
  const b64 = i >= 0 ? dataUrl.slice(i + 1) : dataUrl
  return Math.floor(b64.length * 3 / 4) - (b64.endsWith('==') ? 2 : b64.endsWith('=') ? 1 : 0)
}

/** Add to a plan, within the total the sign-off can carry. Returns the image, or a reason. */
export function attach(plan, { kind, dataUrl, bytes, note = '' }) {
  const have = (plan.images ?? []).reduce((n, i) => n + (i.bytes ?? 0), 0)
  if (have + bytes > MAX_TOTAL) {
    return { ok: false, why: 'That is more pictures than the design can carry. Take one out first.' }
  }
  const image = { id: `img-${Date.now().toString(36)}`, kind, dataUrl, bytes, note, addedAt: new Date().toISOString() }
  plan.images = [...(plan.images ?? []), image]
  return { ok: true, image }
}
